import { useEffect, useState } from "react";
import { ChevronRight, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { deleteInquiry, listInquiries, type InquiryData } from "@/services/inquiry-service";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";
import { ConfirmDialog } from "@/components/common";
import { toast } from "@/hooks/use-toast";
import ArrowRight from "@/assets/icons/ic-arrow-right.svg";
import IcCalendar from "@/assets/icons/ic-calendar.svg";
import IconHelp from "@/assets/icons/icon-help.svg";
import IcRight from "@/assets/icons/ic-arr-right.svg";

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; padding: string }> =
  {
    in_progress: {
      label: "처리 중",
      bg: "bg-[#FFAA72]",
      text: "text-[#000000]",
      padding: "px-[12.5px]",
    },
    completed: {
      label: "답변완료",
      bg: "bg-[#43946C]",
      text: "text-[#000000]",
      padding: "px-[8px]",
    },
  };

export function InquiryCardsSection({
  onDataLoad,
  onLoadComplete,
}: {
  onDataLoad?: (hasData: boolean) => void;
  onLoadComplete?: () => void;
}) {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<InquiryData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const fetchInquiries = async () => {
    try {
      const result = await listInquiries(1, 3);
      setInquiries(result.items);
      onDataLoad?.(result.items.length > 0);
    } catch {
      setInquiries([]);
      onDataLoad?.(false);
    } finally {
      setIsLoading(false);
      onLoadComplete?.();
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteInquiry(deleteTarget);
      toast({ description: "문의가 삭제되었습니다." });
      setDeleteTarget(null);
      fetchInquiries();
    } catch {
      setDeleteTarget(null);
    }
  };

  if (isLoading) return null;
  if (inquiries.length === 0) return null;

  return (
    <section className="flex flex-col gap-0.5 w-full">
      {/* Section Header */}
      <div className="hidden lg:flex items-center h-12 w-full">
        <div className="flex-1">
          <h3 className="text-xl font-bold leading-[28px] tracking-[-0.02em] text-[#000000]">
            문의 이력
          </h3>
        </div>
        <button
          type="button"
          onClick={() => navigate("/inquiries", { state: { from: "my-page" } })}
          className="inline-flex items-center gap-2 text-base font-bold leading-6 tracking-[-0.01em] text-black  transition-opacity"
        >
          View all
          <img src={IcRight} alt="" className="w-6 h-6" />
        </button>
      </div>
      {/* Cards Grid */}
      <div className="flex flex-col gap-3 lg:flex-row lg:gap-6 w-full ">
        {inquiries.map((inquiry) => {
          const config = STATUS_CONFIG[inquiry.status] || STATUS_CONFIG.in_progress;
          return (
            <div
              key={inquiry.id}
              onClick={() => navigate(`/inquiries/${inquiry.id}`)}
              className="w-full lg:w-[357px] lg:h-[148px] cursor-pointer flex flex-col gap-4 lg:gap-3 p-3 pb-4 lg:p-6 border border-[rgba(0,0,0,0.09)] lg:border-[rgba(0,0,0,0.15)] rounded lg:bg-white bg-white text-left hover:shadow-sm transition-shadow"
            >
              {/*Mobile */}
              <div className="w-full lg:hidden flex items-start gap-3">
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <p className="text-base font-semibold leading-6 tracking-[-0.01em] text-[rgba(0,0,0,0.91)] truncate">
                    {inquiry.title}
                  </p>
                  <span className="text-base leading-6 tracking-[-0.01em] text-[#000000]">
                    {formatDate(inquiry.created_at, "YYYY-MM-DD HH:mm:ss")}
                  </span>
                </div>
                <span
                  className={`inline-flex items-center gap-1 w-fit shrink-0 py-[2px] rounded-[4px] text-[14px] leading-[20px] tracking-[-0.01em] ${config.bg} ${config.text} ${config.padding}`}
                >
                  {config.label}
                </span>
              </div>
              {/* Divider - mobile only */}
              <div className="lg:hidden py-1">
                <div className="border-t border-[rgba(0,0,0,0.15)]" />
              </div>
              {/* Delete button - mobile only */}
              <button
                type="button"
                className="lg:hidden w-full h-8 rounded-[4px] bg-[rgba(245,43,0,0.09)] text-xs font-bold leading-3 text-[#000000] px-3 py-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(inquiry.id);
                }}
              >
                삭제
              </button>
              {/* Content: title + description */}
              <div className=" flex-col gap-1 w-full text-[#000000] lg:flex hidden ">
                <span className="text-base font-bold leading-6 tracking-[-0.01em] truncate">
                  {inquiry.title}
                </span>
                {inquiry.content && (
                  <span className="text-sm leading-5 tracking-[-0.01em] line-clamp-2 h-10">
                    {inquiry.content}
                  </span>
                )}
              </div>
              {/* Info: badge + date */}
              <div className=" items-center justify-between w-full lg:flex hidden ">
                <span
                  className={`inline-flex items-center gap-1 py-[2px] rounded ${config.bg} ${config.text} ${config.padding}`}
                >
                  <span className="text-sm leading-5 tracking-[-0.01em]">{config.label}</span>
                </span>
                <span className="inline-flex items-center gap-1 text-xs leading-4 text-black">
                  <img src={IcCalendar} alt="Calendar" />
                  {formatDate(inquiry.created_at, "YYYY-MM-DD")}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {inquiries.length > 0 && (
        <Button
          variant="outline"
          onClick={() => navigate("/inquiries", { state: { from: "my-page" } })}
          className="lg:hidden mt-1 inline-flex items-center justify-center gap-2 w-full h-12 border-none bg-white text-base font-bold leading-6 tracking-[-0.01em] text-black  transition-colors"
        >
          View all
          <img src={ArrowRight} alt="arrow-right" />
        </Button>
      )}
      {/* Delete Confirmation Dialog */}{" "}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        icon={<img src={IconHelp} alt="Icon Alert Circle" />}
        iconVariant="neutral"
        title="문의 삭제"
        description="해당 문의를 삭제하시겠습니까?"
        primaryAction={{ label: "네", onClick: handleDelete }}
        secondaryAction={{
          label: "아니요",
          onClick: () => setDeleteTarget(null),
        }}
      />{" "}
    </section>
  );
}
